import React, { ReactElement, useEffect, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { IntlProvider as BaseIntlProvider } from 'react-intl';

import { getConfig } from './config';
import {
  appLanguagesBasedOnCountry,
  SupportedLanguages,
} from './modules/AppLanguage/constants';

const defaultLanguage = SupportedLanguages.en;

const languageToLocaleMap: { [key: string]: string } = {
  [SupportedLanguages.zhTW]: 'zh-tw',
  [SupportedLanguages.en]: 'en',
  [SupportedLanguages.enSG]: 'en-sg',
  [SupportedLanguages.enMY]: 'en-my',
  [SupportedLanguages.id]: 'id',
  [SupportedLanguages.vi]: 'vi',
};

export const mapLanguageToDayjsLocale = (language: string) =>
  languageToLocaleMap[language] || language;

type IntlProviderProps = {
  children: ReactElement;
};

const IntlProvider = ({ children }: IntlProviderProps) => {
  const config = getConfig();
  const [messages, setMessages] = useState();
  const [locale, setLocale] = useState(
    appLanguagesBasedOnCountry[config.COUNTRY][0] || defaultLanguage
  );

  useEffect(() => {
    async function getMessages() {
      const config = getConfig();
      const country = config.COUNTRY;
      const language =
        config.LANG ||
        appLanguagesBasedOnCountry[country][0] ||
        defaultLanguage;

      const localeJsonName = language;

      const messagesFromS3 = localeJsonName
        ? await axios
            // ?cache=false is to force browser to clear cache after we add Cache-control: 'no-cache'
            // to S3. We can remove the querystring after one month
            .get(
              `https://${config.S3_I18N}/${localeJsonName.replace(
                /-/g,
                '_'
              )}.json?cache=false`
            )
            .then(response => response.data)
            .catch(error =>
              console.error(`[i18n] Invalid locale '${language}'`, error)
            )
        : null;
      setMessages(messages => messagesFromS3 || messages);
      setLocale(messagesFromS3 ? language : defaultLanguage);

      const dateLocale = mapLanguageToDayjsLocale(localeJsonName);
      if (dateLocale) {
        await import(`dayjs/locale/${dateLocale}.js`);
        dayjs.locale(dateLocale);
      }
    }
    getMessages();
  }, []);

  return (
    <BaseIntlProvider
      locale={locale}
      textComponent={React.Fragment}
      messages={messages}
    >
      {children}
    </BaseIntlProvider>
  );
};

export default IntlProvider;
